import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PaperPlaneRight, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

function ConversationList({ conversations, activeId, onSelect }) {
  if (conversations.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <p className="font-heading text-xl text-[#1A1C1A] mb-2">No messages yet</p>
        <p className="text-sm text-[#5C665D]">Start a conversation by messaging an item owner.</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-[#EAE7E0]">
      {conversations.map(c => (
        <button
          key={c.partner_id}
          onClick={() => onSelect(c.partner_id)}
          className={`w-full text-left p-4 hover:bg-[#FDFBF7] transition-colors ${activeId === c.partner_id ? "bg-[#FDFBF7]" : ""}`}
          data-testid={`conversation-${c.partner_id}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2C5545]/10 flex items-center justify-center text-[#2C5545] font-medium text-sm flex-shrink-0">
              {c.partner_name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[#1A1C1A] truncate">{c.partner_name || "Unknown"}</p>
                {c.unread > 0 && <span className="w-5 h-5 bg-[#D96C4A] text-white rounded-full text-[10px] flex items-center justify-center">{c.unread}</span>}
              </div>
              <p className="text-xs text-[#5C665D] truncate mt-0.5">{c.last_message}</p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ChatView({ partnerId, user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!partnerId) return;
    const fetchMessages = () => {
      api.get(`/messages/${partnerId}`).then(r => setMessages(r.data || [])).catch(() => {});
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [partnerId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { receiver_id: partnerId, content: input.trim() });
      setMessages(prev => [...prev, data]);
      setInput("");
    } catch {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  if (!partnerId) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-[#5C665D]">
        Select a conversation
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map(msg => {
          const isMine = msg.sender_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${isMine ? "bg-[#2C5545] text-white rounded-br-md" : "bg-white border border-[#EAE7E0] text-[#1A1C1A] rounded-bl-md"}`} data-testid={`message-${msg.id}`}>
                {msg.content}
              </div>
            </div>
          );
        })}
        <div ref={scrollRef} />
      </div>
      <form onSubmit={send} className="p-4 border-t border-[#EAE7E0] flex gap-2">
        <input
          value={input} onChange={e => setInput(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 bg-[#FDFBF7] border border-[#EAE7E0] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all"
          data-testid="message-input"
        />
        <button type="submit" disabled={sending || !input.trim()} className="w-10 h-10 bg-[#2C5545] text-white rounded-full flex items-center justify-center hover:bg-[#224235] transition-all disabled:opacity-50" data-testid="message-send-btn">
          <PaperPlaneRight size={18} weight="fill" />
        </button>
      </form>
    </div>
  );
}

export default function Messages() {
  const { partnerId: paramPartnerId } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activePartner, setActivePartner] = useState(paramPartnerId || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/messages/conversations").then(r => setConversations(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (paramPartnerId) setActivePartner(paramPartnerId);
  }, [paramPartnerId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight mb-6" data-testid="messages-heading">Messages</h1>
        <div className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-3" style={{ height: "calc(100vh - 220px)" }}>
          {/* Sidebar */}
          <div className={`border-r border-[#EAE7E0] overflow-y-auto ${activePartner ? "hidden md:block" : ""}`}>
            <ConversationList conversations={conversations} activeId={activePartner} onSelect={setActivePartner} />
          </div>
          {/* Chat */}
          <div className={`md:col-span-2 ${!activePartner ? "hidden md:flex" : "flex flex-col"}`}>
            {activePartner && (
              <div className="p-3 border-b border-[#EAE7E0] md:hidden">
                <button onClick={() => setActivePartner(null)} className="flex items-center gap-2 text-sm text-[#5C665D]" data-testid="messages-back-btn">
                  <ArrowLeft size={16} /> Back
                </button>
              </div>
            )}
            <ChatView partnerId={activePartner} user={user} />
          </div>
        </div>
      </div>
    </main>
  );
}
